import { useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { BIDashboardDetailMainContent } from "@/pages/biReports/biReportsDetailsComponents/BIDashboardDetailMainContent";
import { StreamingContent } from "@/modules/streaming";
import { InsightIcon } from "@assets/icons";
import {
  HeatMapDataType,
  DashboardAccessActivity,
  DashboardRefreshActivity,
} from "@/pages/biReports/types";
import { Heatmap } from "@/pages/biReports/biReportsDetailsComponents/Heatmap";
import { DataTable } from "@/components/tables";
import { TableHeaderType, parseSortDirection } from "@/components/tables/types";
import { useQuery } from "@tanstack/react-query";
import { getDashboardAccessActivity, getDashboardRefreshActivity } from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { Paginate } from "@/components/paginate";
import { StreamingContextProvider } from "@/modules/streaming";

const BiReportDetails = () => {
  const { id } = useParams();
  // Breadcrumbs are set in BIDashboardDetailMainContent where dashboard name is fetched
  const [showSideContent, setShowSideContent] = useState(false);
  const [viewsHeatmapData, setViewsHeatmapData] = useState<HeatMapDataType | null>(null);
  const [refreshHeatmapData, setRefreshHeatmapData] = useState<HeatMapDataType | null>(null);
  const [view, setView] = useState<string | undefined>(undefined);
  const [accessPage, setAccessPage] = useState(1);
  const [refreshPage, setRefreshPage] = useState(1);
  const [accessPageSize, setAccessPageSize] = useState(5);
  const [refreshPageSize, setRefreshPageSize] = useState(5);

  const [searchParams] = useSearchParams();

  // Sorting state for access activity (read from URL params)
  const accessSortBy = searchParams.get("accessSortBy") || "date";
  const accessSortOrder = parseSortDirection(searchParams.get("accessSortOrder"), "desc");

  // Sorting state for refresh activity (read from URL params)
  const refreshSortBy = searchParams.get("refreshSortBy") || "date";
  const refreshSortOrder = parseSortDirection(searchParams.get("refreshSortOrder"), "desc");

  // Headers for access activity
  const accessHeaders: TableHeaderType<DashboardAccessActivity>[] = [
    {
      label: "Date",
      uid: "date",
      render: (item: DashboardAccessActivity) => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">{item.date}</span>
      ),
      id: 0,
      width: 150,
      sort: {
        sortSearchKey: "accessSortBy",
        sortOrderSearchKey: "accessSortOrder",
      },
    },
    {
      label: "Views Count",
      uid: "count",
      render: (item: DashboardAccessActivity) => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">{item.count}</span>
      ),
      id: 1,
      width: 120,
      sort: {
        sortSearchKey: "accessSortBy",
        sortOrderSearchKey: "accessSortOrder",
      },
    },
  ];

  // Headers for refresh activity
  const refreshHeaders: TableHeaderType<DashboardRefreshActivity>[] = [
    {
      label: "Date",
      uid: "date",
      render: (item: DashboardRefreshActivity) => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">{item.date}</span>
      ),
      id: 0,
      width: 150,
      sort: {
        sortSearchKey: "refreshSortBy",
        sortOrderSearchKey: "refreshSortOrder",
      },
    },
    {
      label: "Refreshes Count",
      uid: "count",
      render: (item: DashboardRefreshActivity) => (
        <span className="al-text-xs al-text-gray-800 al-font-normal">{item.count}</span>
      ),
      id: 1,
      width: 120,
      sort: {
        sortSearchKey: "refreshSortBy",
        sortOrderSearchKey: "refreshSortOrder",
      },
    },
  ];

  // Query for access activity data
  const { data: accessActivityData, isFetching: isAccessLoading } = useQuery({
    queryKey: [
      "dashboard-access-activity",
      id,
      accessPage,
      accessPageSize,
      accessSortBy,
      accessSortOrder,
    ],
    queryFn: () =>
      getDashboardAccessActivity(id!, {
        page: accessPage,
        page_size: accessPageSize,
        sort_by: accessSortBy,
        sort: accessSortOrder,
      }),
    enabled: showSideContent && view === "views" && !!id,
  });

  // Query for refresh activity data
  const { data: refreshActivityData, isFetching: isRefreshLoading } = useQuery({
    queryKey: [
      "dashboard-refresh-activity",
      id,
      refreshPage,
      refreshPageSize,
      refreshSortBy,
      refreshSortOrder,
    ],
    queryFn: () =>
      getDashboardRefreshActivity(id!, {
        page: refreshPage,
        page_size: refreshPageSize,
        sort_by: refreshSortBy,
        sort: refreshSortOrder,
      }),
    enabled: showSideContent && view === "refreshes" && !!id,
  });

  return (
    <StreamingContextProvider>
      <BIDashboardDetailMainContent
        id={id}
        setShowSideContent={setShowSideContent}
        setSideContentViewsHeatmapData={setViewsHeatmapData}
        setSideContentRefreshHeatmapData={setRefreshHeatmapData}
        setView={setView}
      />
      <Sheet open={showSideContent} onOpenChange={setShowSideContent}>
        <SheetContent className="!al-max-w-[50vw] al-z-[100]">
          <SheetHeader>
            <SheetTitle className="al-sr-only">
              {view === "views" ? "View" : "Refreshes"} Insight
            </SheetTitle>
          </SheetHeader>
          <div className="al-mt-4">
            <Card className="!al-bg-[#eaf3ff] !al-border-action-brand">
              <CardHeader>
                <Stack gap="md" align="center">
                  <InsightIcon className="al-h-6 al-w-6" />
                  <h2 className="al-text-md">{view === "views" ? "View" : "Refreshes"} Insight</h2>
                </Stack>
              </CardHeader>
              <CardContent>
                {view === "views" && viewsHeatmapData && (
                  <Stack
                    className={"al-w-full al-bg-white al-rounded-md al-mb-4 al-p-2"}
                    justify={"start"}
                  >
                    <Tabs defaultValue="graph" className={"al-w-full"}>
                      <TabsList>
                        <TabsTrigger
                          value="graph"
                          className={
                            "data-[state=active]:al-bg-brand data-[state=active]:al-text-white"
                          }
                        >
                          Heatmap
                        </TabsTrigger>
                        <TabsTrigger
                          value="activities"
                          className={
                            "data-[state=active]:al-bg-brand data-[state=active]:al-text-white"
                          }
                        >
                          Activities
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="graph">
                        <Stack
                          gap={"none"}
                          justify={"center"}
                          align={"center"}
                          direction={"column"}
                        >
                          <span className={"al-text-muted-foreground al-self-start"}>
                            Cumulative dashboard views over 30 days by day of week and time of day.
                          </span>
                          <Heatmap
                            height={300}
                            width={500}
                            data={viewsHeatmapData}
                            colorscale="Blues"
                          />
                        </Stack>
                      </TabsContent>
                      <TabsContent value="activities" className={"al-max-h-80 al-overflow-auto"}>
                        {isAccessLoading ? (
                          <Stack align="center" justify="center" className="al-min-h-40">
                            <RelativeComponentLoader label="Loading access activities..." />
                          </Stack>
                        ) : (
                          <>
                            <DataTable
                              header={accessHeaders}
                              data={
                                accessActivityData || {
                                  items: [],
                                  total: 0,
                                  page: 1,
                                  size: 10,
                                  pages: 1,
                                }
                              }
                              minWidth={400}
                            />
                            {accessActivityData && (
                              <Paginate
                                itemCount={accessActivityData.total}
                                page={accessPage - 1}
                                pageSize={accessActivityData.size}
                                numPages={accessActivityData.pages}
                                onPageClick={page => setAccessPage(page + 1)}
                                onPageSizeChange={size => {
                                  setAccessPageSize(size);
                                  setAccessPage(1);
                                }}
                                showPageSize={false}
                              />
                            )}
                          </>
                        )}
                      </TabsContent>
                    </Tabs>
                  </Stack>
                )}
                {view === "views" && !viewsHeatmapData && (
                  <Stack
                    justify={"center"}
                    align={"center"}
                    className={"al-w-full al-bg-white al-rounded-md al-mb-4 al-min-h-52"}
                  >
                    No graph data available
                  </Stack>
                )}
                {view === "refreshes" && refreshHeatmapData && (
                  <Stack
                    className={"al-w-full al-bg-white al-rounded-md al-mb-4 al-p-2"}
                    justify={"start"}
                  >
                    <Tabs defaultValue="graph" className={"al-w-full"}>
                      <TabsList>
                        <TabsTrigger
                          value="graph"
                          className={
                            "data-[state=active]:al-bg-brand data-[state=active]:al-text-white"
                          }
                        >
                          Heatmap
                        </TabsTrigger>
                        <TabsTrigger
                          value="activities"
                          className={
                            "data-[state=active]:al-bg-brand data-[state=active]:al-text-white"
                          }
                        >
                          Activities
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="graph">
                        <Stack
                          gap={"none"}
                          justify={"center"}
                          align={"center"}
                          direction={"column"}
                        >
                          <span className={"al-text-muted-foreground al-self-start"}>
                            Cumulative refreshes over 30 days by day of week and time of day.
                          </span>
                          <Heatmap
                            height={300}
                            width={500}
                            data={refreshHeatmapData}
                            colorscale="Blues"
                          />
                        </Stack>
                      </TabsContent>
                      <TabsContent value="activities" className={"al-max-h-80 al-overflow-auto"}>
                        {isRefreshLoading ? (
                          <Stack align="center" justify="center" className="al-min-h-40">
                            <RelativeComponentLoader label="Loading refresh activities..." />
                          </Stack>
                        ) : (
                          <>
                            <DataTable
                              header={refreshHeaders}
                              data={
                                refreshActivityData || {
                                  items: [],
                                  total: 0,
                                  page: 1,
                                  size: 10,
                                  pages: 1,
                                }
                              }
                              minWidth={400}
                            />
                            {refreshActivityData && (
                              <Paginate
                                itemCount={refreshActivityData.total}
                                page={refreshPage - 1}
                                pageSize={refreshActivityData.size}
                                numPages={refreshActivityData.pages}
                                onPageClick={page => setRefreshPage(page + 1)}
                                onPageSizeChange={size => {
                                  setRefreshPageSize(size);
                                  setRefreshPage(1);
                                }}
                                showPageSize={false}
                              />
                            )}
                          </>
                        )}
                      </TabsContent>
                    </Tabs>
                  </Stack>
                )}
                {view === "refreshes" && !refreshHeatmapData && (
                  <Stack
                    justify={"center"}
                    align={"center"}
                    className={"al-w-full al-bg-white al-rounded-md al-mb-4 al-min-h-52"}
                  >
                    No data available
                  </Stack>
                )}
                <div className="al-p-4 al-rounded-md al-bg-white al-min-h-[200px]">
                  <StreamingContent
                    key={`insight-${id}-${view}`}
                    id={`insight-${id}-${view}`}
                    endpoint={`/business_intelligence/dashboards/insights_stream`}
                    requestBody={{ dashboard_id: id, view: view }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </SheetContent>
      </Sheet>
    </StreamingContextProvider>
  );
};

export default BiReportDetails;
